import {
  Alert,
  AlertTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Typography,
  Box
} from "@mui/material";
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/en-gb";
import { useCallback, useEffect, useState } from "react";
import { downloadReport, exportReport } from "../../services/reports.service";
import {
  DOWNLOAD_BALANCE_SHEET_URL,
  DOWNLOAD_BANK_ACCOUNT_CASH_BOOK_REPORT_URL,
  DOWNLOAD_CASH_FLOW_REPORT_URL,
  DOWNLOAD_BUDGET_VS_ACTUAL_REPORT_URL,
  DOWNLOAD_CLIENT_MOBILE_MONEY_RECONCILIATION_REPORT_URL,
  DOWNLOAD_COLLECTIONS_BY_CAPITAL_FUND_REPORT_URL,
  DOWNLOAD_COLLECTIONS_BY_SERVICE_CHARGE_REPORT_URL,
  DOWNLOAD_COLLECTIONS_BY_UTILITIES_REPORT_URL,
  DOWNLOAD_COLLECTIONS_REPORT_URL,
  DOWNLOAD_CUSTOMER_BALANCES_REPORT_URL,
  DOWNLOAD_CUSTOMER_OUTSTANDING_BALANCES_REPORT_URL,
  DOWNLOAD_DAILY_SALES_REPORT_URL,
  DOWNLOAD_AGING_REPORT_URL,
  DOWNLOAD_VENDOR_AGING_REPORT_URL,
  DOWNLOAD_ELECTRICITY_INCOME_VS_EXPENSE_REPORT_URL,
  DOWNLOAD_EQUIPMENT_MAINTENANCE_REPORT_URL,
  DOWNLOAD_FACILITY_REPORT_URL,
  DOWNLOAD_GENERAL_LEDGER_REPORT_URL,
  DOWNLOAD_INCOME_REPORT_URL,
  DOWNLOAD_MAINTENANCE_COST_ANALYSIS_REPORT_URL,
  DOWNLOAD_MAINTENANCE_HISTORY_REPORT_URL,
  DOWNLOAD_MAINTENANCE_REQUEST_REPORT_URL,
  DOWNLOAD_MANAGEMENT_INCOME_REPORT_URL,
  DOWNLOAD_METER_VARIANCE_REPORT_URL,
  DOWNLOAD_MOBILE_MONEY_REPORT_URL,
  DOWNLOAD_PETTY_CASH_REPORT_URL,
  DOWNLOAD_PORTFOLIO_RENT_ROLL_REPORT_URL,
  DOWNLOAD_RECONCILIATION_REPORT_URL,
  DOWNLOAD_RENT_ROLL_REPORT_URL,
  DOWNLOAD_SECURITY_DEPOSIT_REPORT_URL,
  DOWNLOAD_UTILITY_DEPOSIT_REPORT_URL,
  DOWNLOAD_SMARTKODI_MOBILE_MONEY_REPORT_URL,
  DOWNLOAD_VACANT_UNITS_REPORT_URL,
  DOWNLOAD_VAT_IN_REPORT_URL,
  DOWNLOAD_VAT_OUT_REPORT_URL,
  DOWNLOAD_VAT_REPORT_URL,
  DOWNLOAD_VENDOR_PERFORMANCE_REPORT_URL,
  DOWNLOAD_WATER_INCOME_VS_EXPENSE_REPORT_URL,
  DOWNLOAD_WITHHOLDING_TAX_REPORT_URL,
  DOWNLOAD_WORK_ORDER_REPORT_URL,
  DOWNLOAD_COMPLEX_WORK_ORDER_REPORT_URL,
  DOWNLOAD_PROMASTER_REPORT_URL,
  DOWNLOAD_TRIAL_BALANCE_REPORT_URL,
  DOWNLOAD_BILLS_REPORT_URL,
  DOWNLOAD_RECEIPTS_REPORT_URL,
} from "../../constants/url.constants";
import ModalContainerModule from "../modules/modalContainer";
import { ResponsiveRow } from "../../constants/layout.constants";
import dayjs from "dayjs";
import { listApprovedBudgets } from "../../services/budgets.service";
import { getComplexes } from "../../services/complex.service";
import { getFacilityCourts } from "../../services/courts.service";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import { dateDifference, formatDate, titleCase } from "../../util/util";
import DownloadIcon from "@mui/icons-material/Download";
import { issueInfoMessage, issueWarnMessage } from "../../actions/message";
import { getLoggedInUser } from "../../services/profile.service";
import FormControlLabel from "@mui/material/FormControlLabel";
import MUIAutoCompleteMultiSelectForm from "../inputs/MUIAutoCompleteMultiSelectForm";

/**
 *Required props are:
 - showModal: True or false to show or hide modal respectively.
 - closeModal: Function to close the modal.
 - report: The report to be generated.
 */

export default function GenerateReport(props) {
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [docType, setDocType] = useState("pdf");
  const [proReportType, setProReportType] = useState("Utilities");
  // Will be set to true if we want a report with commission expected on all income - Not Rent income only.
  const [allIncome, setAllIncome] = useState(false);
  const [commissionRate, setCommissionRate] = useState("0");
  const [approvedBudgets, setApprovedBudgets] = useState([]);
  const [emailDoc, setEmailDoc] = useState(false);
  const [consolidatedReport, setConsolidatedReport] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [selectedComplex, setSelectedComplex] = useState("");
  const [complexOptions, setComplexOptions] = useState([]);
  const [courtOptions, setCourtOptions] = useState([]);
  const [selectedCourtIds, setSelectedCourtIds] = useState([]);
  let reportFormatOptions = [
    { value: "pdf", label: "PDF" },
    { value: "csv", label: "CSV" },
  ];
  let proMasterReportOptions = [
    {value: "Utilities", label: "Utilities"},
    {value: "Invoices", label: "Invoices"},
    {value: "Receipts", label: "Receipts"},
    {value: "Facilities", label: "Facilities"}
  ]

  const getSessionUserEmail = useCallback(async function () {
    const user = await getLoggedInUser();
    return user?.email;
  }, []);

  useEffect(() => {
    async function fetchUserEmail() {
      const email = await getSessionUserEmail();
      setUserEmail(email);
    }
    fetchUserEmail();
  }, [getSessionUserEmail]);

  useEffect(() => {
    setFrom(dayjs().subtract(3, "month"));
    setTo(dayjs());
  }, []);

  useEffect(() => {
    async function fetchComplexes() {
      try {
        const complexes = await getComplexes();
        const complexOptions = complexes.map(complex => ({
          value: complex.id,
          label: complex.name
        }));
        setComplexOptions(complexOptions);
      } catch (error) {
        console.error("Error fetching complexes:", error);
        setComplexOptions([]);
      }
    }

    async function fetchCourts() {
      try {
        const courts = await getFacilityCourts();
        const courtOptions = courts.map(court => ({
          value: court.id,
          label: court.name
        }));
        setCourtOptions(courtOptions);
      } catch (error) {
        console.error("Error fetching courts:", error);
        setCourtOptions([]);
      }
    }
    fetchComplexes();
    fetchCourts();
  }, []);

  useEffect(() => {
    if (
      props.report === "Rent Roll" ||
      props.report === "Client Mobile Money Statement" ||
      props.report === "Collections" ||
      props.report === "Collections By Capital Fund" ||
      props.report === "Collections By Service Charge" ||
      props.report === "Collections By Utilities"
    ) {
      setDocType("csv");
    }

    if (props.report === "Budget Vs Actual") {
      listApprovedBudgets().then((data) => {
        setApprovedBudgets(data);
      });
    }
  }, [props]);

  // If the report is "Rent Roll", override the reportFormatOptions
  if (
    props.report === "Rent Roll" ||
    props.report === "Collections" ||
    props.report === "Collections By Capital Fund" ||
    props.report === "Collections By Service Charge" ||
    props.report === "Collections By Utilities"
  ) {
    reportFormatOptions = [{ value: "csv", label: "CSV" }];
  }

  function modalClose() {
    props.closeModal();
    let dateOffset = 24 * 60 * 60 * 1000 * 90;
    let daysAgo = dayjs() - dateOffset;
    setFrom(daysAgo);
    setTo(dayjs());
    setDocType("pdf");
    setProReportType("Utilities")
    setCommissionRate("");
    setAllIncome(false);
    setSelectedComplex("");
    setEmailDoc(false);
    setSelectedCourtIds([]);
    setConsolidatedReport(false)
  }

  function validateDates(data) {
    let { from, to } = data;
    if (props.report === "Collections") {
      const fromDate = new Date(from);
      const toDate = new Date(to);
      const updatedFrom = new Date(
        fromDate.getFullYear(),
        fromDate.getMonth(),
        1
      );
      const updatedTo = new Date(
        toDate.getFullYear(),
        toDate.getMonth() + 1,
        0
      );
      from = dayjs(updatedFrom);
      to = dayjs(updatedTo);
    }

    if (from && to && from.isAfter(to)) {
      issueWarnMessage("From date cannot be after To date");
      return false;
    }
    const fromDateStr = from ? from.format("DD MMM YYYY") : "";
    const toDateStr = to ? to.format("DD MMM YYYY") : "";

    if (dateDifference(fromDateStr, toDateStr) > 365) {
      issueWarnMessage("Max 1 year date range");
      return false;
    }

    if (props.report === "Rent Roll" && to && to.isAfter(dayjs())) {
      issueWarnMessage("To date cannot be after today");
      return false;
    }

    if (from && from.isAfter(dayjs())) {
      issueWarnMessage("From date cannot be in the future");
      return false;
    }

    return true;
  }

  function handleStartDateChange(value) {
    setFrom(value);
  }

  function handleEndDateChange(value) {
    setTo(value);
  }

  function handleCourtChange(value) {
    setSelectedCourtIds(value);
  }

  function handleConsolidatedChange(value) {
    setConsolidatedReport(value);
    if(value){
      setSelectedCourtIds([]);
    }
  }

  function generateReport() {
    let data = {
      from: dayjs(from),
      to: to,
      docType: docType,
      emailDoc: false,
      download: false,
      courtIds: selectedCourtIds.map((op) => op.value),
    };

    // let userEmail = getSessionUserEmail();

    let managementincomeData = {
      from: dayjs(from),
      to: to,
      docType: docType,
      commissionRate: commissionRate,
      commissionOnIncome: allIncome,
    };
    
    let agingData = {
      docType: docType,
      asAtDate: dayjs(to ?? dayjs()).toISOString(),
    };
    if (props.report === "Vendor Aging Report") {
      agingData.consolidated = consolidatedReport;
    }

    if(["Petty Cash", "Income", "Balance Sheet", "Trial Balance", "Utility Deposit", "Bills Report", "Cash Flow", "Reconciliation", "Vendor Aging Report"].includes(props.report)) {
      data.consolidated = consolidatedReport;
    }
    if (props.report === "Balance Sheet") {
      downloadReport(DOWNLOAD_BALANCE_SHEET_URL, data);
      props.closeModal();
    }
    if (props.report === "Budget Vs Actual") {
      downloadReport(DOWNLOAD_BUDGET_VS_ACTUAL_REPORT_URL, data);
      props.closeModal();
    }
    if (
      props.report === "Collections" ||
      props.report === "Collections By Capital Fund" ||
      props.report === "Collections By Service Charge" ||
      props.report === "Collections By Utilities"
    ) {
      if (validateDates(data)) {
        if (emailDoc) {
          data.emailDoc = true;
          let message;
          let url;
          switch (props.report) {
            case "Collections":
              url = DOWNLOAD_COLLECTIONS_REPORT_URL;
              message = `The Collections report is being exported and will be sent to your email (${userEmail}). Please wait few minutes for the email to reach your mailbox.`;
              break;
            case "Collections By Capital Fund":
              url = DOWNLOAD_COLLECTIONS_BY_CAPITAL_FUND_REPORT_URL;
              message = `The Collection By Capital Fund report is being exported and will be sent to your email (${userEmail}). Please wait few minutes for the email to reach your mailbox.`;
              break;
            case "Collections By Service Charge":
              url = DOWNLOAD_COLLECTIONS_BY_SERVICE_CHARGE_REPORT_URL;
              message = `The Collection By Service Charge report is being exported and will be sent to your email (${userEmail}). Please wait few minutes for the email to reach your mailbox.`;
              break;
            case "Collections By Utilities":
              url = DOWNLOAD_COLLECTIONS_BY_UTILITIES_REPORT_URL;
              message = `The Collection By Utilities report is being exported and will be sent to your email (${userEmail}). Please wait few minutes for the email to reach your mailbox.`;
              break;
            default:
              url = DOWNLOAD_COLLECTIONS_REPORT_URL;
              message = `The Collections report is being exported and will be sent to your email (${userEmail}). Please wait few minutes for the email to reach your mailbox.`;
              break;
          }
          issueInfoMessage(message);
          exportReport(url, data);
        } else {
          data.download = true;
          let url;
          let message;
          switch (props.report) {
            case "Collections":
              url = DOWNLOAD_COLLECTIONS_REPORT_URL;
              message = `The Collections report is being downloaded.`;
              break;
            case "Collections By Capital Fund":
              url = DOWNLOAD_COLLECTIONS_BY_CAPITAL_FUND_REPORT_URL;
              message = `The Collection By Capital Fund report is being downloaded.`;
              break;
            case "Collections By Service Charge":
              url = DOWNLOAD_COLLECTIONS_BY_SERVICE_CHARGE_REPORT_URL;
              message = `The Collection By Service Charge report is being downloaded.`;
              break;
            case "Collections By Utilities":
              url = DOWNLOAD_COLLECTIONS_BY_UTILITIES_REPORT_URL;
              message = `The Collection By Utilities report is being downloaded.`;
              break;
            default:
              url = DOWNLOAD_COLLECTIONS_REPORT_URL;
              message = `The Collections report is being downloaded.`;
              break;
          }
          issueInfoMessage(message);
          downloadReport(url, data);
        }
        modalClose();
      }
    }
    if (props.report === "Customer Balances") {
      downloadReport(DOWNLOAD_CUSTOMER_BALANCES_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Daily Sales") {
      downloadReport(DOWNLOAD_DAILY_SALES_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Aging Report" || props.report === "Vendor Aging Report") {
      const agingUrl =
        props.report === "Vendor Aging Report"
          ? DOWNLOAD_VENDOR_AGING_REPORT_URL
          : DOWNLOAD_AGING_REPORT_URL;
      issueInfoMessage(`The ${props.report} is being downloaded.`);
      downloadReport(agingUrl, agingData);
      modalClose();
    }
    if (props.report === "Electricity") {
      downloadReport(DOWNLOAD_ELECTRICITY_INCOME_VS_EXPENSE_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Facility") {
      downloadReport(DOWNLOAD_FACILITY_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Income") {
      downloadReport(DOWNLOAD_INCOME_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Petty Cash") {
      downloadReport(DOWNLOAD_PETTY_CASH_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Bank Account Cash Book") {
      downloadReport(DOWNLOAD_BANK_ACCOUNT_CASH_BOOK_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Cash Flow") {
      downloadReport(DOWNLOAD_CASH_FLOW_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Work Order") {
      downloadReport(DOWNLOAD_WORK_ORDER_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Complex Work Order Report") {
      if (!selectedComplex) {
        issueWarnMessage("Please select a complex");
        return;
      }
      data.complexId = selectedComplex;
      downloadReport(DOWNLOAD_COMPLEX_WORK_ORDER_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Maintenance Request") {
      downloadReport(DOWNLOAD_MAINTENANCE_REQUEST_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Equipment Maintenance") {
      downloadReport(DOWNLOAD_EQUIPMENT_MAINTENANCE_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Management") {
      downloadReport(
        DOWNLOAD_MANAGEMENT_INCOME_REPORT_URL,
        managementincomeData
      );
      props.closeModal();
      modalClose();
    }
    if (props.report === "Meter Variance") {
      downloadReport(DOWNLOAD_METER_VARIANCE_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Mobile Money") {
    let message;
    if (emailDoc) {
    data.emailDoc = true;
     message = `The Mobile Money report is being exported and will be sent to your email (${userEmail}). Please wait few minutes for the email to reach your mailbox.`;
    }else{
     data.download = true;
     message = `The Mobile Money report is being downloaded.`;
    }
     issueInfoMessage(message);
      downloadReport(DOWNLOAD_MOBILE_MONEY_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Receipts") {
      let message;
      if (emailDoc) {
        data.emailDoc = true;
        message = `The Receipts report is being exported and will be sent to your email (${userEmail}). Please wait few minutes for the email to reach your mailbox.`;
      }else{
        data.download = true;
        message = `The Receipts report is being downloaded.`;
      }
      issueInfoMessage(message);
      downloadReport(DOWNLOAD_RECEIPTS_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Outstanding Balances") {
      downloadReport(DOWNLOAD_CUSTOMER_OUTSTANDING_BALANCES_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Portfolio") {
      downloadReport(DOWNLOAD_PORTFOLIO_RENT_ROLL_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Reconciliation") {
      downloadReport(DOWNLOAD_RECONCILIATION_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Rent Roll") {
      if (validateDates(data)) {
        downloadReport(DOWNLOAD_RENT_ROLL_REPORT_URL, data);
        props.closeModal();
      }
    }
    if (props.report === "Smartkodi Mobile Money") {
      downloadReport(DOWNLOAD_SMARTKODI_MOBILE_MONEY_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Vacant Units") {
      downloadReport(DOWNLOAD_VACANT_UNITS_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "VAT") {
      downloadReport(DOWNLOAD_VAT_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Withholding Tax") {
      downloadReport(DOWNLOAD_WITHHOLDING_TAX_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "VAT In") {
      downloadReport(DOWNLOAD_VAT_IN_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "VAT Out") {
      downloadReport(DOWNLOAD_VAT_OUT_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Water") {
      downloadReport(DOWNLOAD_WATER_INCOME_VS_EXPENSE_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Client Mobile Money Statement") {
      issueInfoMessage("The statement will be downloaded shortly");
      data.from = new Date(from).getTime();
      data.to = new Date(to).getTime();
      downloadReport(
        DOWNLOAD_CLIENT_MOBILE_MONEY_RECONCILIATION_REPORT_URL,
        data
      );
      props.closeModal();
    }
    if (props.report === "Vendor Report") {
      downloadReport(DOWNLOAD_VENDOR_PERFORMANCE_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Maintenance History Report") {
      downloadReport(DOWNLOAD_MAINTENANCE_HISTORY_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Maintenance Cost Analysis Report") {
      downloadReport(DOWNLOAD_MAINTENANCE_COST_ANALYSIS_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "General Ledger Report") {
      downloadReport(DOWNLOAD_GENERAL_LEDGER_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Security Deposit") {
      downloadReport(DOWNLOAD_SECURITY_DEPOSIT_REPORT_URL, data);
      props.closeModal();
    }
    if (props.report === "Utility Deposit") {
      downloadReport(DOWNLOAD_UTILITY_DEPOSIT_REPORT_URL, data);
      props.closeModal();
    }
    if(props.report === "ProMaster Report"){
      data.docType = "csv"
      data.reportType = proReportType
      downloadReport(DOWNLOAD_PROMASTER_REPORT_URL, data);
      props.closeModal();
    }
    if(props.report === "Trial Balance") {
      downloadReport(DOWNLOAD_TRIAL_BALANCE_REPORT_URL, data);
      props.closeModal();
    }
    if(props.report === "Bills Report") {
      downloadReport(DOWNLOAD_BILLS_REPORT_URL, data);
      props.closeModal();
    }
  }

  //For management Report
  const getFromAllIncome = (event) => {
    let value = event.target.checked;
    setAllIncome(value);
  };

  const downloadBudgetReport = (item) => {
    let budgetReportData = {
      from: dayjs(from),
      to: to,
      docType: docType,
      budgetId: item && item.id,
    };
    downloadReport(DOWNLOAD_BUDGET_VS_ACTUAL_REPORT_URL, budgetReportData);
    props.closeModal();
  };

  const hasMonthPicker = (props.report === "Collections" || props.report === "ProMaster Report")

  return (
    <>
      {props.report === "Budget Vs Actual" ? (
        <ModalContainerModule
          size="sm"
          cancel={modalClose}
          openModal={props.showModal}
          modalTitle="Approved Budgets"
        >
          <ResponsiveRow container sx={{ height: "auto" }}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Date Created</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Income Total</TableCell>
                    <TableCell>Expense Total</TableCell>
                    <TableCell>Download</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvedBudgets &&
                    approvedBudgets.length > 0 &&
                    approvedBudgets.map((row) => {
                      return (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {row.name}
                          </TableCell>
                          <TableCell>{formatDate(row.dateCreated)}</TableCell>
                          <TableCell>{formatDate(row.startDate)}</TableCell>
                          <TableCell>{formatDate(row.endDate)}</TableCell>
                          <TableCell>{row.incomeTotal}</TableCell>
                          <TableCell>{row.expenseTotal}</TableCell>
                          <TableCell>
                            <IconButton
                              aria-label="Example"
                              onClick={() => {
                                downloadBudgetReport(row);
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </ResponsiveRow>
        </ModalContainerModule>
      ) : (
        <ModalContainerModule
          size="small"
          accept={generateReport}
          cancel={modalClose}
          openModal={props.showModal}
          ProMaster ReportmodalTitle={`Generate ${props.report} Report`}
          acceptButtonText={
            props.report === "Collections" ||
            props.report === "Collections By Capital Fund" ||
            props.report === "Collections By Service Charge" ||
            props.report === "Collections By Utilities"
              ? "Download"
              : "Generate"
          }
        >
          <ResponsiveRow
            sx={{
              width: "100%",
              height: "auto",
              alignItems: "center",
              flexDirection: { xs: "column", lg: "row" },
              flexWrap: "nowrap",
              padding: 0,
            }}
            container
          >
            <ResponsiveRow
              sx={{
                width: { xs: "100%", lg: "50%" },
                height: "auto",
                flexDirection: "column",
                flexWrap: "nowrap",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: 0,
              }}
              container
            >
              <Alert severity="info" sx={{ width: "100%", height: "100%" }}>
                <AlertTitle>About {titleCase(props.report)} Report</AlertTitle>
                <Box>
                  {props.description}
                  
                  {(props.report === "Collections") && (
                    <>
                    <br/><br/>
                      <span
                        sx={{
                          fontSize: "1rem",
                          lineHeight: "30px",
                          margin: { xs: "10px auto", lg: "0px 0 5px 5px" },
                          color: "#333",
                          fontWeight: 500,
                          textAlign: "start",
                          whiteSpace: "pre-wrap",
                          paddingLeft: "20px"
                        }}
                      >Take note of the following:</span>
                      <ul style={{paddingLeft: "60px"}}>
                        <li>This report reflects activity by invoice generation date; consequently, charges billed early will appear in the month they were created rather than the month they are due.
                          <br />
                          <b>Example:</b><br/>
                          <ul>
                            <li>May invoice generated on 28th April → Appears under April Increase.</li>
                            <li>May invoice generated on 1st May → Appears under May Increase.</li>
                          </ul>
                        </li>
                      </ul>
                    </>
                  )}
                </Box>
              </Alert>
            </ResponsiveRow>
            <ResponsiveRow
              sx={{
                width: { xs: "100%", lg: "50%" },
                height: "auto",
                alignItems: "center",
              }}
              container
            >
              {props &&
                props.report !== "Vacant Units" &&
                props.report !== "Equipment Maintenance" &&
                props.report !== "Client Mobile Money Statement" &&
                props.report !== "Security Deposit" &&
                props.report !== "Utility Deposit" &&
                proReportType !== "Facilities" && (
                  <ResponsiveRow
                    sx={{
                      width: "100%",
                      flexDirection: {
                        xs: "column",
                        lg: "row",
                      },
                      flexWrap: "nowrap",
                    }}
                  >
                    {props.report !== "Trial Balance" &&
                      props.report !== "Aging Report" &&
                      props.report !== "Vendor Aging Report" && (
                      <FormControl
                        item
                        sx={{
                          width: "100%",
                          marginBottom: "10px",
                        }}
                      >
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale="en-gb"
                        >
                          <DatePicker
                            label={props.report === "ProMaster Report" ? "Month" : "Start date"}
                            sx={{
                              width: {
                                xs: "100%",
                                lg: "90%",
                              },
                              marginRight: "10px",
                            }}
                            id={"startDate"}
                            value={dayjs(from)}
                            maxDate={dayjs().endOf("day")}
                            views={
                              hasMonthPicker
                                ? ["month", "year"]
                                : undefined
                            }
                            isrequired
                            onChange={(newDate) => handleStartDateChange(newDate)}
                          />
                        </LocalizationProvider>
                      </FormControl>
                    )}
                    {props.report !== "ProMaster Report" && (
                      <FormControl
                        item
                        sx={{
                          width: "100%",
                          marginBottom: "10px",
                        }}
                      >
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale="en-gb"
                        >
                          <DatePicker
                            label={
                              props.report === "Trial Balance" ||
                              props.report === "Aging Report" ||
                              props.report === "Vendor Aging Report"
                                ? "As Of"
                                : "End date"
                            }
                            id={"endDate"}
                            value={dayjs(to)}
                            maxDate={dayjs().endOf("day")}
                            views={
                              hasMonthPicker
                                ? ["month", "year"]
                                : undefined
                            }
                            sx={{
                              width: {
                                xs: "100%",
                                lg: "90%",
                              },
                              marginRight: "10px",
                            }}
                            isrequired
                            onChange={(newDate) => handleEndDateChange(newDate)}
                          />
                        </LocalizationProvider>
                      </FormControl>
                    )}
                  </ResponsiveRow>
                )}
              {props && props.report === "Client Mobile Money Statement" && (
                <ResponsiveRow
                  sx={{
                    width: "100%",
                    flexDirection: {
                      xs: "column",
                      lg: "column",
                    },
                    flexWrap: "nowrap",
                  }}
                >
                  <FormControl
                    item
                    sx={{
                      width: "100%",
                      marginBottom: "10px",
                    }}
                  >
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="en-gb"
                    >
                      <DateTimePicker
                        label="Start date"
                        sx={{
                          width: {
                            xs: "100%",
                            lg: "90%",
                          },
                          marginRight: "10px",
                        }}
                        id={"startDate"}
                        value={dayjs(from)}
                        maxDate={dayjs().endOf("day")}
                        views={[
                          "year",
                          "month",
                          "day",
                          "hours",
                          "minutes",
                          "seconds",
                        ]}
                        isrequired
                        onChange={(newDate) => handleStartDateChange(newDate)}
                      />
                    </LocalizationProvider>
                  </FormControl>
                  <FormControl
                    item
                    sx={{
                      width: "100%",
                      marginBottom: "10px",
                    }}
                  >
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="en-gb"
                    >
                      <DateTimePicker
                        label="End date"
                        id={"endDate"}
                        value={dayjs(to)}
                        maxDate={dayjs().endOf("day")}
                        views={[
                          "year",
                          "month",
                          "day",
                          "hours",
                          "minutes",
                          "seconds",
                        ]}
                        sx={{
                          width: {
                            xs: "100%",
                            lg: "90%",
                          },
                          marginRight: "10px",
                        }}
                        isrequired
                        onChange={(newDate) => handleEndDateChange(newDate)}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </ResponsiveRow>
              )}
              {props.report === "Management" && (
                <>
                  <InputLabel>All Income?</InputLabel>
                  <Switch
                    checked={allIncome}
                    onChange={(event) => getFromAllIncome(event)}
                    inputProps={{
                      "aria-label": "controlled",
                    }}
                  />

                  <FormControl
                    item
                    sx={{
                      width: "100%",
                      margin: "auto 10px 10px 10px",
                    }}
                  >
                    <Typography
                      sx={{
                        marginTop: "20px",
                        marginBottom: "20px",
                      }}
                    >
                      If your Management Income includes commission on rent
                      collected, enter the percentage of commission and press
                      Submit.
                    </Typography>
                  </FormControl>

                  <FormControl
                    item
                    sx={{ width: "100%", margin: "auto 10px 10px 10px" }}
                  >
                    <InputLabel>Commission Rate</InputLabel>
                    <OutlinedInput
                      label="Commission Rate"
                      id="commission"
                      sx={{ width: { xs: "100%", lg: "90%" } }}
                      onChange={(event) => {
                        let value = event.target.value;
                        setCommissionRate(value);
                      }}
                      value={commissionRate}
                      inputProps={
                        <TextField
                          sx={{
                            width: { xs: "100%", md: "90%" },
                          }}
                        />
                      }
                    />
                  </FormControl>
                </>
              )}{" "}
              {props.report !== "ProMaster Report" && (
                <FormControl
                  item
                  sx={{ width: "100%", margin: "auto 10px 10px 10px" }}
                >
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    label="Document Type"
                    sx={{ width: { xs: "100%", lg: "90%" } }}
                    id={"documentType"}
                    disabled={props.report === "Client Mobile Money Statement"}
                    value={docType}
                    onChange={(event) => setDocType(event.target.value)}
                  >
                    {reportFormatOptions && reportFormatOptions.length > 0 ? (
                      reportFormatOptions.map((format) => {
                        if (
                          props.report === "Collections" &&
                          format.value === "pdf"
                        ) {
                          return null;
                        }
                        return (
                          <MenuItem key={format.value} value={format.value}>
                            {format.label}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}
              {props.report === "ProMaster Report" && (
                <FormControl
                  item
                  sx={{ width: "100%", margin: "auto 10px 10px 10px" }}
                >
                  <InputLabel>Report Option</InputLabel>
                  <Select
                    label="Report Option"
                    sx={{ width: { xs: "100%", lg: "90%" } }}
                    id={"reportOption"}
                    value={proReportType}
                    onChange={(event) => setProReportType(event.target.value)}
                  >
                    {proMasterReportOptions && proMasterReportOptions.length > 0 ? (
                      proMasterReportOptions.map((report) => {
                        return (
                          <MenuItem key={report.value} value={report.value}>
                            {report.label}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}
              {props.report === "Complex Work Order Report" && (
                <FormControl
                  item
                  sx={{ width: "100%", margin: "auto 10px 10px 10px" }}
                >
                  <InputLabel>Complex Selection</InputLabel>
                  <Select
                    label="Complex Selection"
                    sx={{ width: { xs: "100%", lg: "90%" } }}
                    id={"complexSelection"}
                    value={selectedComplex}
                    onChange={(event) => setSelectedComplex(event.target.value)}
                  >
                    <MenuItem value="" disabled>
                      Select Complex
                    </MenuItem>
                    {complexOptions && complexOptions.length > 0 ? (
                      complexOptions.map((complex) => (
                        <MenuItem key={complex.value} value={complex.value}>
                          {complex.label}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}
              {(["Petty Cash", "Income", "Balance Sheet", "Trial Balance", "Utility Deposit", "Bills Report", "Cash Flow", "Reconciliation", "Vendor Aging Report"].includes(props.report)) && (
                  <FormControl
                      item
                      sx={{ width: "100%", margin: "auto 10px 10px 10px" }}
                  >
                    <FormControlLabel
                        control={
                          <Switch
                              checked={consolidatedReport}
                              onChange={(event) =>
                                  handleConsolidatedChange(event.target.checked)
                              }
                              name="consolidated report"
                          />
                        }
                        label="Report for all Facilities"
                    />
                  </FormControl>
              )}
              {
                  courtOptions.length > 0 &&
                  !consolidatedReport &&
                  (
                      props.report === "Collections" ||
                      props.report === "Collections By Capital Fund" ||
                      props.report === "Collections By Service Charge" ||
                      props.report === "Collections By Utilities" ||
                      props.report === "Mobile Money" ||
                      props.report === "Income" ||
                      props.report === "Petty Cash" ||
                      props.report === "Cash Flow" ||
                      props.report === "VAT" ||
                      props.report === "Withholding Tax" ||
                      props.report === "General Ledger Report" ||
                      props.report === "Utility Deposit"
                  ) && (
                      <FormControl
                          item
                          sx={{ width: "100%", margin: "auto 10px 10px 10px" }}
                      >
                        <MUIAutoCompleteMultiSelectForm
                            options={courtOptions}
                            title="Court(s)"
                            label="Court(s)"
                            placeholder="Select Court(s)"
                            selectedOptions={selectedCourtIds}
                            isRequired={true}
                            disabled={courtOptions.length < 1}
                            onSelect={(event, newValue) => handleCourtChange(newValue)}
                            sx={{ width: { xs: "100%", lg: "90%" } }}
                            limitTags={4}
                        />
                      </FormControl>
                   )
              }
              {(props.report === "Collections" ||
                props.report === "Collections By Capital Fund" ||
                props.report === "Collections By Service Charge" ||
                props.report === "Collections By Utilities") && (
                <FormControl
                  item
                  sx={{ width: "100%", margin: "auto 10px 10px 10px" }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={emailDoc}
                        onChange={(event) => setEmailDoc(event.target.checked)}
                        name="emailDoc"
                      />
                    }
                    label="Export to Email"
                  />
                </FormControl>
              )}
            </ResponsiveRow>
          </ResponsiveRow>
        </ModalContainerModule>
      )}
    </>
  );
}
